import { useState } from "react";
import { toast } from "react-toastify";
import { Ban, Check, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";

import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { FormDialog, Field } from "../../features/web3/forex/components/FormDialog";

import { formatDateTime } from "../../lib/format";
import { usePermissions } from "../../hooks/usePermissions";
import {
  useBdReviewList,
  useApproveBdReview,
  useRejectBdReview,
  SOCIAL_FIELDS,
} from "./bdReviewApi";

const COLUMNS = [
  { label: "Forwarded" },
  { label: "Applicant" },
  { label: "Sponsor" },
  { label: "What fell short" },
  { label: "", align: "right", sticky: true },
];

const displayName = (u) =>
  [u?.name, u?.surname].filter(Boolean).join(" ") || "—";

/** Name, username, email and whatever social links the user has filled in. */
function Person({ user }) {
  const links = SOCIAL_FIELDS.filter((f) => user?.socialLinks?.[f.key]);
  return (
    <div className="space-y-1">
      <div className="font-medium">{displayName(user)}</div>
      <div className="font-mono text-xs text-muted-foreground">
        {user?.username || "—"}
      </div>
      {user?.email ? (
        <div className="text-xs text-muted-foreground">{user.email}</div>
      ) : null}
      {links.length ? (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {links.map((f) => (
            <a
              key={f.key}
              href={user.socialLinks[f.key]}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-white/[0.02] px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              {f.label}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      ) : (
        // The social presence is the whole thing being judged, so its absence
        // is a finding rather than an empty cell.
        <div className="text-xs text-amber-400">No social links</div>
      )}
    </div>
  );
}

export default function BdVerification() {
  const { can } = usePermissions();
  const list = useBdReviewList();
  const approve = useApproveBdReview();
  const reject = useRejectBdReview();

  const [confirming, setConfirming] = useState(null); // { row, action }
  const [note, setNote] = useState("");

  const rows = list.data;
  const busy = approve.isPending || reject.isPending;

  const canApprove = can("POST:/api/admin/user/bd-requests/review/approve");
  const canReject = can("POST:/api/admin/user/bd-requests/review/reject");

  const close = () => {
    setConfirming(null);
    setNote("");
  };

  const run = async (mutation, verb) => {
    try {
      await mutation.mutateAsync({
        requestId: confirming.row.requestId,
        note: note.trim() || undefined,
      });
      toast.success(`BD request ${verb}`);
      close();
    } catch (err) {
      // Sponsor capacity is re-read now, so "Please wait until your sponsor has
      // available spot" lands here as a normal answer, not a fault.
      toast.error(err?.response?.data?.message || `${verb} failed`);
      close();
    }
  };

  return (
    <QueueLayout
      eyebrow="Users"
      title="BD"
      accent="verification"
      description="BD invitations that were accepted but missed the numbers. Judge the social presence and decide."
      isFetching={list.isFetching}
      onRefresh={() => list.refetch()}
    >
      {list.error ? (
        <ErrorBanner error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <QueueTable
          columns={COLUMNS}
          isLoading={list.isLoading}
          rows={rows}
          renderRow={(row) => (
            <tr
              key={row.requestId}
              className="align-top transition-colors hover:bg-white/[0.02]"
            >
              <Td className="whitespace-nowrap font-mono text-muted-foreground">
                {formatDateTime(row.forwardedOn)}
              </Td>
              <Td>
                <Person user={row.referral} />
              </Td>
              <Td>
                <Person user={row.sponsor} />
              </Td>
              {/* A single '; '-joined string, unlike the array the user-facing
                  side gets — render it as it comes. */}
              <Td className="text-xs text-muted-foreground">
                {row.shortfall || "—"}
              </Td>
              <Td align="right" sticky>
                <div className="flex justify-end gap-2">
                  {canApprove && (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => setConfirming({ row, action: "approve" })}
                    >
                      <Check className="h-3.5 w-3.5" />
                      Approve
                    </Button>
                  )}
                  {canReject && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => setConfirming({ row, action: "reject" })}
                    >
                      <Ban className="h-3.5 w-3.5" />
                      Reject
                    </Button>
                  )}
                </div>
              </Td>
            </tr>
          )}
        />
      )}

      <FormDialog
        open={confirming?.action === "approve"}
        onOpenChange={(v) => !v && close()}
        title="Approve BD request"
        description="Converts the applicant to BD. Their sponsor needs a free slot, which is checked again now."
        onSubmit={() => run(approve, "approved")}
        submitLabel="Approve"
        loading={approve.isPending}
      >
        <Field label="Note (optional)">
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Kept on the request for whoever reads it next"
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={confirming?.action === "reject"}
        onOpenChange={(v) => !v && close()}
        title="Reject BD request"
        description="The applicant stays an NU."
        onSubmit={() => run(reject, "rejected")}
        submitLabel="Reject"
        loading={reject.isPending}
        danger
      >
        <Field label="Note (optional)">
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Shown to the user with the rejection"
          />
        </Field>
      </FormDialog>
    </QueueLayout>
  );
}
